import { JSX } from 'react';
import classNames from 'classnames';

// components
import { AtIcon } from '@/components/atoms/at-icon/at-icon';
import { AtMedia } from '@/components/atoms/at-media/at-media';
import { AtText } from '@/components/atoms/at-text/at-text';
import { AtClickable } from '@/components/atoms/at-clickable/at-clickable';

// types
import { EIcons } from '@/components/atoms/at-icon/icons';
import { IImageGlobalCSProps, IImageProps } from '@/lib/contentstack-sdk/types';
import {
  EClickableSize,
  EClickableTypes,
} from '@/components/atoms/at-clickable/at-clickable.types';
import { ETextSize } from '@/components/atoms/at-text/at-text.types';
import {
  IRenderImageProps,
  IMlTestimonialBlockCard,
} from './ml-testimonial-block-card.types';

// utils
import { parseImageFromCMS } from '@/lib/contentstack-sdk/utils/parseHelpers';
import { AtHeader } from '@/components/atoms';

const RenderImage = ({ image }: IRenderImageProps) => {
  const imageProps = parseImageFromCMS(image);
  return imageProps?.src && <AtMedia {...imageProps} />;
};

/**
 * A testimonial card component used exclusively inside `OrTestimonialBlock`.
 * Renders a quote, an optional author headline, overlapping author images, and a CTA.
 *
 */

export const MlTestimonialBlockCard = ({
  cta,
  headline,
  images,
  quote,
}: IMlTestimonialBlockCard): JSX.Element => {
  const { label, href, variant } = cta;
  return (
    <div>
      <div className="flex flex-col gap-2 text-32 lg:flex-row">
        <AtIcon icon={EIcons.QUOTE} color="var(--color-ui-stroke-tertiary)" />

        <AtText
          type="p"
          size={ETextSize.LG}
          className="text-2xl text-skin-disabled"
        >
          {headline}
        </AtText>
      </div>
      <AtHeader variant="h3" element="p" className="my-8">
        {quote}
      </AtHeader>
      <div className="flex flex-col gap-4 md:flex-row md:items-center">
        <div className="flex">
          {images?.map((img: IImageProps | IImageGlobalCSProps, i: number) => {
            return (
              <div
                key={`${img?._metadata?.uid}-${i}`}
                className={classNames(
                  'relative flex h-92 w-92 items-center justify-center overflow-hidden rounded-full md:h-134 md:w-134',
                  i === 1 && '-ml-5',
                )}
              >
                <RenderImage image={img} />
              </div>
            );
          })}
        </div>

        <AtClickable
          label={label}
          className="w-fit"
          href={href}
          variant={variant}
          size={EClickableSize.LG}
          type={EClickableTypes.LINK}
          interactLocation="ml-testimonial-block"
        />
      </div>
    </div>
  );
};
